import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import {
  ShieldCheck, Eye, Zap, Fingerprint, BadgeCheck, GitCompare, IdCard, Sparkles,
} from "lucide-react";

const ICONS = {
  "shield-check": ShieldCheck,
  "eye": Eye,
  "zap": Zap,
  "id-card": IdCard,
  "git-compare": GitCompare,
  "fingerprint": Fingerprint,
  "badge-check": BadgeCheck,
};

export default function SmartServices() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [bookNumber, setBookNumber] = useState("");
  const [declaredValue, setDeclaredValue] = useState(0);
  const [protectionOption, setProtectionOption] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [c, o, s] = await Promise.all([
      api.get("/smart-services/catalog"),
      api.get("/smart-services/orders"),
      api.get("/shipments"),
    ]);
    setCatalog(c.data); setOrders(o.data); setShipments(s.data);
  };
  useEffect(() => { load(); }, []);

  const openPicker = (s) => {
    setPickerFor(s);
    setBookNumber("");
    setDeclaredValue(0);
    setProtectionOption(s.default_protection || "");
  };

  const order = async () => {
    if (!pickerFor) return;
    setLoading(true);
    try {
      await api.post("/smart-services/orders", {
        service_code: pickerFor.code,
        book_number: bookNumber || null,
        stage: bookNumber ? "post" : "pre",
        declared_value: parseFloat(declaredValue) || 0,
        protection_option: protectionOption || pickerFor.default_protection || null,
      });
      toast.success(`${pickerFor.name} activated`);
      setPickerFor(null); setBookNumber(""); setDeclaredValue(0); setProtectionOption("");
      await refresh(); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const activePercent = useMemo(() => {
    if (!pickerFor) return 0;
    if (pickerFor.protection_options) {
      const code = protectionOption || pickerFor.default_protection || pickerFor.protection_options[0].code;
      const opt = pickerFor.protection_options.find((o) => o.code === code);
      return opt?.percent || 0;
    }
    return pickerFor.variable_percent || 0;
  }, [pickerFor, protectionOption]);

  const computedPrice = useMemo(() => {
    if (!pickerFor) return 0;
    let p = pickerFor.price;
    if (activePercent) p += (parseFloat(declaredValue) || 0) * activePercent;
    return Math.round(p * 100) / 100;
  }, [pickerFor, declaredValue, activePercent]);

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
          <Sparkles size={12} /> Smart Services
        </div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Protect every shipment.</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Activate any service before or after booking. Charges are auto-debited from your wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-10">
        {catalog.map((s) => {
          const Icon = ICONS[s.icon] || ShieldCheck;
          return (
            <div key={s.code} data-testid={`service-card-${s.code}`}
              className={`relative bg-white border rounded-sm p-5 flex flex-col ${s.flagship ? "border-[var(--brand-accent)] shadow-md" : "border-zinc-200"}`}>
              {s.flagship && <div className="absolute -top-2 left-4 text-[10px] font-bold tracking-[0.2em] uppercase brand-bg text-white px-2 py-0.5 rounded-sm">Flagship</div>}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: `${s.color}18`, color: s.color }}>
                  <Icon size={20} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold">{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{s.tagline}</div>
                </div>
              </div>
              <p className="text-sm text-zinc-600 mt-3 leading-relaxed flex-1">{s.description}</p>
              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between">
                <div>
                  <div className="font-mono-plex font-semibold text-zinc-900" style={{ color: s.color }}>
                    ${s.price.toFixed(2)}
                    {s.protection_options && <span className="text-xs text-zinc-500 font-normal"> + 1.00–1.25%</span>}
                    {!s.protection_options && s.variable_percent && <span className="text-xs text-zinc-500 font-normal"> + {(s.variable_percent * 100).toFixed(1)}%</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                    {s.stages.includes("pre") && "Pre-ship"}{s.stages.includes("pre") && s.stages.includes("post") ? " + " : ""}{s.stages.includes("post") && "Post-ship"}
                  </div>
                </div>
                <button
                  data-testid={`activate-${s.code}`}
                  onClick={() => openPicker(s)}
                  className="brand-bg text-white text-xs font-medium px-3 py-1.5 rounded-sm hover:opacity-90">
                  Activate
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5 sm:p-6">
        <div className="font-display text-xl font-bold mb-4">Active service orders</div>
        {orders.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-sm">No service orders yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left">Service</th>
                  <th className="text-left">Protection</th>
                  <th className="text-left">Stage</th>
                  <th className="text-left">Shipment</th>
                  <th className="text-left">Status</th>
                  <th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-zinc-100" data-testid={`order-${o.order_id}`}>
                    <td className="py-2 font-mono-plex text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="font-medium">{o.service_name}</td>
                    <td className="text-xs">{o.protection_label || "—"}</td>
                    <td><span className="text-xs uppercase tracking-wider text-zinc-600">{o.stage}-ship</span></td>
                    <td className="font-mono-plex text-xs">{o.book_number || "—"}</td>
                    <td><span className="px-2 py-0.5 rounded-sm text-xs font-medium uppercase tracking-wider bg-emerald-50 text-emerald-700">{o.status}</span></td>
                    <td className="text-right font-mono-plex">${o.price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activation modal */}
      {pickerFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm overflow-y-auto" onClick={() => setPickerFor(null)}>
          <div className="bg-white rounded-md max-w-md w-full p-6 my-4" onClick={(e) => e.stopPropagation()} data-testid="activate-modal">
            <div className="font-display text-xl font-bold">Activate {pickerFor.name}</div>
            <p className="text-sm text-zinc-600 mt-1">{pickerFor.tagline}</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">Apply to shipment (optional)</label>
                <select
                  data-testid="activate-shipment-select"
                  value={bookNumber}
                  onChange={(e) => setBookNumber(e.target.value)}
                  className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)] text-sm">
                  <option value="">Pre-ship (not yet booked)</option>
                  {shipments.filter((s) => pickerFor.stages.includes(s.status === "BOOKED" ? "post" : "pre") || pickerFor.stages.includes("post")).map((s) => {
                    const label = `${s.book_number} - ${s.carrier_name} > ${s.receiver.city}`;
                    return <option key={s.book_number} value={s.book_number}>{label}</option>;
                  })}
                </select>
              </div>

              {pickerFor.protection_options && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Protection tier</label>
                  <div className="mt-2 space-y-2">
                    {pickerFor.protection_options.map((opt) => {
                      const code = protectionOption || pickerFor.default_protection || pickerFor.protection_options[0].code;
                      const selected = code === opt.code;
                      return (
                        <label key={opt.code} data-testid={`protection-option-${opt.code}`}
                          className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-all ${selected ? "border-[var(--brand-accent)] bg-blue-50/40" : "border-zinc-200 hover:border-zinc-300"}`}>
                          <input type="radio" name="protection" value={opt.code} checked={selected}
                            onChange={() => setProtectionOption(opt.code)} className="mt-1" />
                          <div className="flex-1">
                            <div className="flex justify-between items-baseline gap-2">
                              <span className="font-display font-bold text-sm text-zinc-900">{opt.label}</span>
                              <span className="font-mono-plex text-xs font-semibold brand-text">+{(opt.percent * 100).toFixed(2)}%</span>
                            </div>
                            <div className="text-xs text-zinc-600 mt-0.5">{opt.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {(pickerFor.protection_options || pickerFor.variable_percent) && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">Declared item value (USD)</label>
                  <input
                    data-testid="activate-declared-value"
                    type="number" min="0" value={declaredValue}
                    onChange={(e) => setDeclaredValue(e.target.value)}
                    className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)] text-sm" />
                  <div className="text-xs text-zinc-500 mt-1">
                    Processing fee ${pickerFor.price.toFixed(2)} + {(activePercent * 100).toFixed(2)}% of declared value.
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                <span className="text-sm text-zinc-600">Total to charge wallet</span>
                <span className="font-display font-black text-2xl" data-testid="activate-total">${computedPrice.toFixed(2)}</span>
              </div>
              <div className="text-xs text-zinc-500">Wallet balance: <span className="font-mono-plex">${(user?.wallet_balance || 0).toFixed(2)}</span></div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setPickerFor(null)} className="flex-1 border border-zinc-300 py-2 rounded-sm text-sm">Cancel</button>
              <button data-testid="confirm-activate-btn" onClick={order} disabled={loading}
                className="flex-1 brand-bg text-white py-2 rounded-sm text-sm disabled:opacity-50">
                {loading ? "Activating…" : "Activate & charge wallet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
